import Analytics from '@/utils/analytics';
import BottomSheet, { BottomSheetFlatList } from '@gorhom/bottom-sheet';
import i18n from '@/utils/i18n';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ActionSheet } from '@/components/action-sheet';
import {
  AffordanceHandleIndicator,
  BottomSheetBackdrop,
} from '@/components/bottom-sheet-backdrop';
import { Avatar } from '@/components/avatar';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { captureException } from '@/utils/sentry';
import { createStyles } from '@/contexts/theme-context';
import { FlatList, Modal, Pressable, View } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import {
  SearchableDropdownMenu,
  SearchableDropdownMenuItemData,
} from '@usepepper/ui/molecules/searchable-dropdown-menu';
import { SearchInput } from '@usepepper/ui/atoms/search-input';
import { SmartOrderAssignableGroup } from '@/types';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useBottomSheetBackHandler } from '@/hooks/use-bottom-sheet-back-handler';
import {
  useGrantSmartOrderRestaurantAccess,
  useSmartOrderAssignableEmployees,
} from './queries';
import { useLayoutContext } from '@/contexts/layout-context';
import { useRenderInDraggableSheet } from '@/utils/item-details';
import { useToasts } from '@/contexts/toast-context';

// Half sheet on open; scrolling the list or typing a search expands it to
// (near) full, matching the item-details sheet's snap points.
const ASSIGN_SHEET_SNAP_POINTS = ['50%', '90%'];

const GROUP_LABEL_KEY: Record<SmartOrderAssignableGroup, string> = {
  [SmartOrderAssignableGroup.Available]: 'employee_assignment.available',
  [SmartOrderAssignableGroup.NeedsAccess]: 'employee_assignment.needs_access',
  [SmartOrderAssignableGroup.RecentlyActive]:
    'employee_assignment.recently_active',
};

interface AssignableEmployeeRow {
  employeeId: string;
  group: SmartOrderAssignableGroup;
  hasRestaurantAccess: boolean;
  name: string;
}

interface Props {
  // The caller's row can be gated on hover, which would otherwise unmount
  // this component -- and the open menu with it -- as soon as the pointer
  // moves off the trigger toward the menu itself. Lets a caller keep the
  // row mounted while this reports itself open.
  onOpenChange?: (isOpen: boolean) => void;
  onSelectEmployee: (employeeUUID: string) => Promise<void>;
  restaurantName: string | null;
  smartOrderUUID: string;
  supplierUUID: string;
  trigger: (open: () => void) => React.ReactElement;
}

export const SmartOrderAssignDropdown: React.FC<Props> = ({
  restaurantName,
  smartOrderUUID,
  supplierUUID,
  trigger,
  onOpenChange,
  onSelectEmployee,
}) => {
  const [styles, theme] = useStyles();
  const { user } = useAuthenticatedUserContext();
  const { showErrorToast } = useToasts();
  const { isSmallLayout } = useLayoutContext();
  // @gorhom/bottom-sheet doesn't work on web (it crashes on findNodeHandle),
  // so the draggable snap-point sheet is native-only; web small layouts fall
  // back to a plain BaseActionSheet with the same content, matching the
  // established pattern for every other draggable sheet in this app.
  const renderInDraggableSheet = useRenderInDraggableSheet();

  const bottomSheetRef = useRef<BottomSheet>(null);
  const { handleSheetPositionChange } =
    useBottomSheetBackHandler(bottomSheetRef);

  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [assigningEmployeeId, setAssigningEmployeeId] = useState<string | null>(
    null,
  );
  const [giveAccessTarget, setGiveAccessTarget] =
    useState<AssignableEmployeeRow | null>(null);
  const [grantingAccess, setGrantingAccess] = useState(false);
  // Employees granted access this session render as available immediately,
  // without waiting on a refetch of the whole roster.
  const [locallyGrantedIds, setLocallyGrantedIds] = useState<Set<string>>(
    new Set(),
  );

  const [fetchAssignableEmployees, { data, error, loading }] =
    useSmartOrderAssignableEmployees();
  const [grantRestaurantAccess] = useGrantSmartOrderRestaurantAccess();

  // Otherwise a forbidden/not-found response looks identical to a genuinely
  // empty roster -- the list just renders "no results" either way.
  useEffect(() => {
    if (error) {
      showErrorToast();
    }
  }, [error, showErrorToast]);

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setSearchText('');
    void fetchAssignableEmployees({
      variables: { smartOrderUUID, supplierUUID },
    });
  }, [fetchAssignableEmployees, smartOrderUUID, supplierUUID]);

  const handleClose = useCallback(() => {
    if (giveAccessTarget) {
      // Confirm dialog owns dismissal while it's open.
      return;
    }
    setIsOpen(false);
    setSearchText('');
  }, [giveAccessTarget]);

  // The sheet stays mounted behind the Modal's `visible` toggle, so its
  // internal position needs resetting back to the half-open snap point on
  // each reopen -- otherwise it reopens wherever a prior pan-to-close or
  // search-triggered expand left it.
  useEffect(() => {
    if (isOpen && renderInDraggableSheet) {
      bottomSheetRef.current?.collapse();
    }
  }, [isOpen, renderInDraggableSheet]);

  const handleSearch = useCallback(
    (text: string) => {
      setSearchText(text);
      // Typing a query is as much intent to scan the full roster as
      // scrolling is, so jump straight to the expanded sheet for it too.
      if (text && renderInDraggableSheet) {
        bottomSheetRef.current?.expand();
      }
    },
    [renderInDraggableSheet],
  );

  const rows = useMemo<AssignableEmployeeRow[]>(() => {
    const employees =
      data?.getSmartOrderAssignableEmployees.assignable_employees;
    if (!employees) {
      return [];
    }
    return employees.map((employee) => ({
      employeeId: employee.employee_id,
      group: locallyGrantedIds.has(employee.employee_id)
        ? SmartOrderAssignableGroup.Available
        : employee.group,
      hasRestaurantAccess:
        employee.has_restaurant_access ||
        locallyGrantedIds.has(employee.employee_id),
      name: employee.name,
    }));
  }, [data, locallyGrantedIds]);

  const filteredRowsByGroup = useMemo(() => {
    const lowerSearch = searchText.trim().toLowerCase();
    const filtered = lowerSearch
      ? rows.filter((row) => row.name.toLowerCase().includes(lowerSearch))
      : rows;

    const grouped: Partial<Record<SmartOrderAssignableGroup, typeof filtered>> =
      {};
    for (const row of filtered) {
      (grouped[row.group] ??= []).push(row);
    }
    return grouped;
  }, [rows, searchText]);

  const listData: SearchableDropdownMenuItemData<AssignableEmployeeRow>[] =
    useMemo(() => {
      const order: SmartOrderAssignableGroup[] = [
        SmartOrderAssignableGroup.RecentlyActive,
        SmartOrderAssignableGroup.Available,
        SmartOrderAssignableGroup.NeedsAccess,
      ];
      return order.flatMap(
        (group) =>
          filteredRowsByGroup[group]?.map((row) => ({
            ...row,
            key: row.employeeId,
          })) ?? [],
      );
    }, [filteredRowsByGroup]);

  const handleSelect = useCallback(
    async (row: AssignableEmployeeRow) => {
      // onSelectEmployee reports failure via toast itself; nothing further to
      // branch on here, so close either way rather than leave the dropdown
      // open with no indication of what happened.
      setAssigningEmployeeId(row.employeeId);
      await onSelectEmployee(row.employeeId);
      setAssigningEmployeeId(null);
      setIsOpen(false);
      setSearchText('');
    },
    [onSelectEmployee],
  );

  const handleConfirmGiveAccess = useCallback(async () => {
    if (!giveAccessTarget) {
      return;
    }
    setGrantingAccess(true);
    try {
      Analytics.track('order_inbounds.assign_dropdown.give_access_press', {
        employeeUUID: giveAccessTarget.employeeId,
        smartOrderUUID,
      });
      const result = await grantRestaurantAccess({
        variables: {
          employeeUUID: giveAccessTarget.employeeId,
          smartOrderUUID,
          supplierUUID,
        },
      });
      // has_restaurant_access can come back false -- e.g. this supplier has no
      // chat with the restaurant at all -- so success alone doesn't mean the
      // row is now assignable.
      if (result.data?.grantSmartOrderRestaurantAccess.has_restaurant_access) {
        setLocallyGrantedIds((prev) =>
          new Set(prev).add(giveAccessTarget.employeeId),
        );
      } else {
        showErrorToast();
      }
    } catch (err) {
      captureException(err);
      showErrorToast();
    } finally {
      setGrantingAccess(false);
      setGiveAccessTarget(null);
    }
  }, [
    giveAccessTarget,
    grantRestaurantAccess,
    showErrorToast,
    smartOrderUUID,
    supplierUUID,
  ]);

  const renderEmployeeRow = (
    item: SearchableDropdownMenuItemData<AssignableEmployeeRow>,
  ) => {
    const isSelf = item.employeeId === user.uuid;
    const isAssigning = assigningEmployeeId === item.employeeId;
    const groupLabel = GROUP_LABEL_KEY[item.group];
    const isFirstInGroup =
      listData.find((row) => row.group === item.group)?.key === item.key;

    return (
      <View key={item.key}>
        {isFirstInGroup ? (
          <View style={styles.groupLabel}>
            <Typography
              color={theme.sk.color.text.secondary}
              textTransform="uppercase"
              variant="paragraph-sm-600"
            >
              {i18n.t(groupLabel)}
            </Typography>
          </View>
        ) : null}
        <Pressable
          disabled={!item.hasRestaurantAccess || isAssigning}
          onPress={() =>
            item.hasRestaurantAccess ? handleSelect(item) : undefined
          }
          style={({ hovered }) => [
            styles.row,
            hovered && item.hasRestaurantAccess && styles.rowHovered,
          ]}
        >
          <Avatar
            containerStyle={
              item.hasRestaurantAccess ? undefined : styles.rowDisabled
            }
            name={item.name}
            size="xsmall"
            uuid={item.employeeId}
          />
          <Typography
            color={
              item.hasRestaurantAccess
                ? theme.sk.color.text.primary
                : theme.sk.color.text.disabled
            }
            flexShrink={1}
            variant="paragraph-md"
          >
            {item.name}
            {isSelf ? ` (${i18n.t('employee_assignment.you')})` : ''}
          </Typography>
          {!item.hasRestaurantAccess ? (
            <Pressable
              hitSlop={8}
              onPress={(e) => {
                e.stopPropagation();
                setGiveAccessTarget(item);
              }}
              style={styles.giveAccessLink}
            >
              <Typography
                color={theme.sk.color.text.blue}
                variant="paragraph-sm-600"
              >
                {i18n.t('employee_assignment.give_access')}
              </Typography>
            </Pressable>
          ) : null}
        </Pressable>
      </View>
    );
  };

  const emptyStateText = loading
    ? i18n.t('loading')
    : error
      ? i18n.t('toast.standard_error')
      : i18n.t('employee_assignment.no_results');

  const listEmptyComponent = <AssignDropdownEmptyState text={emptyStateText} />;

  return (
    <>
      {isSmallLayout ? (
        <>
          {trigger(handleOpen)}
          {renderInDraggableSheet ? (
            <Modal onRequestClose={handleClose} transparent visible={isOpen}>
              <GestureHandlerRootView style={styles.gestureHandler}>
                <BottomSheet
                  backdropComponent={BottomSheetBackdrop}
                  enablePanDownToClose
                  handleComponent={AffordanceHandleIndicator}
                  index={0}
                  onChange={handleSheetPositionChange}
                  onClose={handleClose}
                  ref={bottomSheetRef}
                  snapPoints={ASSIGN_SHEET_SNAP_POINTS}
                >
                  <View style={styles.sheetSearchContainer}>
                    <SearchInput
                      onSearch={handleSearch}
                      placeholderText={i18n.t(
                        'employee_assignment.search_placeholder',
                      )}
                      searchText={searchText}
                    />
                  </View>
                  <BottomSheetFlatList
                    data={listData}
                    keyExtractor={(item) => item.key}
                    ListEmptyComponent={listEmptyComponent}
                    renderItem={({ item }) => renderEmployeeRow(item)}
                  />
                </BottomSheet>
              </GestureHandlerRootView>
            </Modal>
          ) : (
            <BaseActionSheet
              isVisible={isOpen}
              onClose={handleClose}
              testID="smart-order-assign-bottom-sheet"
            >
              <View style={styles.sheetSearchContainer}>
                <SearchInput
                  onSearch={handleSearch}
                  placeholderText={i18n.t(
                    'employee_assignment.search_placeholder',
                  )}
                  searchText={searchText}
                />
              </View>
              <FlatList
                data={listData}
                keyExtractor={(item) => item.key}
                ListEmptyComponent={listEmptyComponent}
                renderItem={({ item }) => renderEmployeeRow(item)}
              />
            </BaseActionSheet>
          )}
        </>
      ) : (
        <SearchableDropdownMenu
          dropdownContainerOverrides={styles.dropdownContainer}
          isVisible={isOpen}
          listData={listData}
          listEmptyComponent={listEmptyComponent}
          onClose={handleClose}
          onSearch={handleSearch}
          placeholderText={i18n.t('employee_assignment.search_placeholder')}
          position="bottom-start"
          renderItem={(item) => renderEmployeeRow(item)}
          searchText={searchText}
          trigger={trigger(handleOpen)}
        />
      )}
      {giveAccessTarget ? (
        <ActionSheet
          actions={[
            i18n.t('cancel'),
            i18n.t('employee_assignment.give_access'),
          ]}
          isVisible
          loadingActionIndex={grantingAccess ? 1 : null}
          message={i18n.t(
            'employee_assignment.give_access.confirmation_message',
            {
              employeeName: giveAccessTarget.name,
              restaurantName: restaurantName ?? '',
            },
          )}
          onAction={async (_action, index) => {
            if (index === 1) {
              await handleConfirmGiveAccess();
            } else {
              setGiveAccessTarget(null);
            }
          }}
          onClose={() => setGiveAccessTarget(null)}
          primaryIndices={[1]}
          testID="smart-order-give-access-action-sheet"
          title={i18n.t('employee_assignment.give_access.confirmation_title', {
            employeeName: giveAccessTarget.name,
            restaurantName: restaurantName ?? '',
          })}
        />
      ) : null}
    </>
  );
};

const AssignDropdownEmptyState: React.FC<{ text: string }> = ({ text }) => {
  const [styles, theme] = useStyles();
  return (
    <View style={styles.emptyContainer}>
      <Typography color={theme.sk.color.text.secondary} variant="paragraph-md">
        {text}
      </Typography>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  dropdownContainer: {
    gap: theme.sk.spacing.spaceXsmall,
    padding: theme.sk.spacing.spaceSmall,
    width: 280,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 24,
  },
  gestureHandler: {
    flex: 1,
  },
  giveAccessLink: {
    marginLeft: 'auto',
  },
  groupLabel: {
    paddingHorizontal: 8,
    paddingTop: 12,
  },
  row: {
    alignItems: 'center',
    borderRadius: theme.sk.radii.radiusSmall,
    flexDirection: 'row',
    gap: 8,
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  rowDisabled: {
    opacity: 0.4,
  },
  rowHovered: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
  sheetSearchContainer: {
    paddingBottom: theme.sk.spacing.spaceSmall,
    paddingHorizontal: theme.sk.spacing.spaceSmall,
  },
}));
